import React, { useEffect, useMemo, useState } from "react";
import {
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Platform,
  useWindowDimensions,
} from "react-native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import ImageCarousel from "../../pages/home/gallery/ImageCarousel";

export type GalleryEditableImageTarget = {
  targetType: "clinic_patient_image";
  targetId: number;
};

export type GalleryImageMetadata = {
  procedure_types?: string[] | null;
  procedure_types_human_readable?: string[] | null;
  view_type?: string | null;
  view_type_human_readable?: string | null;
  surgery_stage?: string | null;
  external_id?: string | null;
  tags?: string[] | null;
  created_at?: string | null;
  updated_at?: string | null;
  date_taken?: string | null;
};

interface GalleryModalProps {
  visible: boolean;
  imageUrls: string[];
  thumbnailUrls?: string[];
  selectedIndex: number;
  onClose: () => void;
  onIndexChange?: (newIndex: number) => void;
  title?: string;
  enableRotation?: boolean;
  shareLinkForIndex?: (index: number) => string | null;
  editableImages?: Array<GalleryEditableImageTarget | null>;
  showImageMetadata?: boolean;
  imageMetadata?: Array<GalleryImageMetadata | null>;
  onSaveEdit?: (params: {
    target: GalleryEditableImageTarget;
    degrees: number;
  }) => Promise<void>;
  onEditComplete?: () => void;
}

const tokens = {
  color: {
    text: "#0F172A",
    muted: "#64748B",
    surface: "#FFFFFF",
    border: "#E2E8F0",
    focus: "#2563EB",
    overlay: "rgba(2, 6, 23, 0.72)",
    controlSurface: "#F8FAFC",
    primary: "#00B7F4",
    primaryText: "#FFFFFF",
    selectedControlSurface: "#E0F7FE",
    panelSurface: "#F8FAFC",
  },
  spacing: {
    xs: 4,
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
  },
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 22,
  },
  shadow: {
    none: 0,
  },
  font: {
    base: "Urbanist600",
  },
  fontSize: {
    xs: 12,
    sm: 14,
    md: 16,
    lg: 18,
  },
  lineHeight: {
    sm: 20,
    md: 24,
  },
  zIndex: {
    base: 1,
  },
  motion: {
    microMs: 100,
    standardMs: 200,
    complexMs: 300,
  },
};

const normalizeDegrees = (degrees: number) => ((degrees % 360) + 360) % 360;

const getTargetKey = (target?: GalleryEditableImageTarget | null) => {
  if (!target) return null;
  return `${target.targetType}:${target.targetId}`;
};

const formatDateOnly = (value?: string | null) => {
  if (!value) return "";

  const valueString = String(value);
  const isoDateMatch = valueString.match(/^(\d{4})-(\d{2})-(\d{2})/);

  if (isoDateMatch) {
    const date = new Date(
      Number(isoDateMatch[1]),
      Number(isoDateMatch[2]) - 1,
      Number(isoDateMatch[3]),
    );

    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }

  const parsedDate = new Date(valueString);
  if (Number.isNaN(parsedDate.getTime())) return valueString;

  return parsedDate.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatMetadataValue = (
  value: string | string[] | null | undefined,
  formatter?: (value: string) => string,
) => {
  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "";
  }

  if (!value) return "";
  return formatter ? formatter(value) : value;
};

const buildMetadataRows = (metadata?: GalleryImageMetadata | null) => {
  if (!metadata) return [];

  return [
    {
      label: "Procedure",
      value: formatMetadataValue(
        metadata.procedure_types_human_readable?.length
          ? metadata.procedure_types_human_readable
          : metadata.procedure_types,
      ),
    },
    {
      label: "View",
      value: formatMetadataValue(
        metadata.view_type_human_readable || metadata.view_type,
      ),
    },
    {
      label: "Surgery stage",
      value: formatMetadataValue(metadata.surgery_stage),
    },
    {
      label: "External ID",
      value: formatMetadataValue(metadata.external_id),
    },
    {
      label: "Tags",
      value: formatMetadataValue(metadata.tags),
    },
    {
      label: "Created",
      value: formatMetadataValue(metadata.created_at, formatDateOnly),
    },
    {
      label: "Updated",
      value: formatMetadataValue(metadata.updated_at, formatDateOnly),
    },
    {
      label: "Date taken",
      value: formatMetadataValue(metadata.date_taken, formatDateOnly),
    },
  ].filter((row) => Boolean(row.value));
};

const getDateSummary = (metadata?: GalleryImageMetadata | null) => {
  if (!metadata) return "";

  const dateTaken = formatDateOnly(metadata.date_taken);
  if (dateTaken) return `Date taken: ${dateTaken}`;

  const createdAt = formatDateOnly(metadata.created_at);
  return createdAt ? `Created at: ${createdAt}` : "";
};

const GalleryModal: React.FC<GalleryModalProps> = ({
  visible,
  imageUrls,
  thumbnailUrls,
  selectedIndex,
  onClose,
  onIndexChange,
  title = "Photos",
  enableRotation = false,
  shareLinkForIndex,
  editableImages,
  showImageMetadata = false,
  imageMetadata,
  onSaveEdit,
  onEditComplete,
}) => {
  const { width: viewportWidth } = useWindowDimensions();
  const [rotations, setRotations] = useState<number[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(selectedIndex);
  const [shareMessage, setShareMessage] = useState<string>("");
  const [mode, setMode] = useState<"view" | "edit">("view");
  const [isMetadataVisible, setIsMetadataVisible] = useState(false);
  const [saving, setSaving] = useState(false);
  const [jobStatusMessage, setJobStatusMessage] = useState("");
  const [
    persistedPendingRotationsByTarget,
    setPersistedPendingRotationsByTarget,
  ] = useState<Record<string, number>>({});

  const currentEditTarget = editableImages?.[currentIndex] ?? null;
  const currentMetadata = imageMetadata?.[currentIndex] ?? null;
  const metadataRows = useMemo(
    () => buildMetadataRows(currentMetadata),
    [currentMetadata],
  );
  const dateSummary = useMemo(
    () => getDateSummary(currentMetadata),
    [currentMetadata],
  );
  const currentTargetKey = getTargetKey(currentEditTarget);
  const currentRotation = normalizeDegrees(rotations[currentIndex] ?? 0);
  const persistedRotation = currentTargetKey
    ? normalizeDegrees(persistedPendingRotationsByTarget[currentTargetKey] ?? 0)
    : 0;
  const hasUnsavedRotation =
    mode === "edit" && currentRotation !== persistedRotation;
  const canEdit = Boolean(currentEditTarget && onSaveEdit);
  const canShowMetadata = Boolean(showImageMetadata && metadataRows.length);
  const imageUrlSignature = useMemo(() => imageUrls.join("|"), [imageUrls]);
  const requestedIndex = Math.min(
    Math.max(Number.isFinite(selectedIndex) ? selectedIndex : 0, 0),
    Math.max(imageUrls.length - 1, 0),
  );

  useEffect(() => {
    setRotations(new Array(imageUrls.length).fill(0));
    setPersistedPendingRotationsByTarget({});
  }, [imageUrls.length, imageUrlSignature]);

  useEffect(() => {
    setCurrentIndex(requestedIndex);
  }, [requestedIndex, imageUrls.length, imageUrlSignature, visible]);

  useEffect(() => {
    setShareMessage("");
    setJobStatusMessage("");
  }, [currentIndex, visible]);

  useEffect(() => {
    if (!visible) {
      setMode("view");
      setIsMetadataVisible(false);
      setSaving(false);
      setJobStatusMessage("");
    }
  }, [visible]);

  useEffect(() => {
    if (Platform.OS !== "web" || !visible || typeof document === "undefined") {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose, visible]);

  useEffect(() => {
    if (mode !== "edit") return;
    if (!canEdit) setMode("view");
  }, [canEdit, mode]);

  const setCurrentRotation = (degrees: number) => {
    setRotations((prev) => {
      const next = [...prev];
      next[currentIndex] = normalizeDegrees(degrees);
      return next;
    });
  };

  const handleRotateLeft = () => {
    if (!imageUrls.length || saving) return;
    setCurrentRotation(currentRotation - 90);
  };

  const handleRotateRight = () => {
    if (!imageUrls.length || saving) return;
    setCurrentRotation(currentRotation + 90);
  };

  const handleStartEdit = () => {
    if (!canEdit) return;
    setMode("edit");
    setIsMetadataVisible(false);
    setJobStatusMessage("");
    setCurrentRotation(persistedRotation);
  };

  const handleCancelEdit = () => {
    setCurrentRotation(persistedRotation);
    setMode("view");
    setSaving(false);
    setJobStatusMessage("");
  };

  const handleSaveEdit = async () => {
    if (!currentEditTarget || !onSaveEdit || !currentTargetKey) return;
    const degrees = normalizeDegrees(currentRotation - persistedRotation);
    if (degrees === 0) return;

    setSaving(true);
    setJobStatusMessage("Saving changes...");
    setPersistedPendingRotationsByTarget((prev) => ({
      ...prev,
      [currentTargetKey]: currentRotation,
    }));

    try {
      await onSaveEdit({ target: currentEditTarget, degrees });
      setMode("view");
      setJobStatusMessage("");
      onEditComplete?.();
    } catch (error) {
      setCurrentRotation(persistedRotation);
      setPersistedPendingRotationsByTarget((prev) => ({
        ...prev,
        [currentTargetKey]: persistedRotation,
      }));
      setMode("edit");
      setJobStatusMessage(
        error instanceof Error
          ? error.message
          : "Unable to save changes. Try again.",
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCopyShareLink = async () => {
    const shareLink = shareLinkForIndex?.(currentIndex);
    if (!shareLink) {
      setShareMessage("Share link unavailable.");
      return;
    }

    try {
      if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareLink);
        setShareMessage("Photo link copied to clipboard.");
      } else {
        setShareMessage("Clipboard unavailable — copy manually.");
      }
    } catch {
      setShareMessage("Clipboard unavailable — copy manually.");
    }
  };

  if (!visible || imageUrls.length === 0) return null;

  const metadataPanelWidth = viewportWidth < 768 ? 240 : 300;

  const renderModalBody = (carouselKey: string) => (
    <React.Fragment>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>{title}</Text>
        {dateSummary ? (
          <Text
            style={styles.headerDateSummary}
            numberOfLines={1}
            ellipsizeMode="tail"
          >
            {dateSummary}
          </Text>
        ) : null}
        <View style={styles.headerControls}>
          {canShowMetadata && mode === "view" ? (
            <TouchableOpacity
              onPress={() => setIsMetadataVisible((current) => !current)}
              style={[
                styles.iconButton,
                isMetadataVisible && styles.activeIconButton,
              ]}
              accessibilityRole="button"
              accessibilityLabel={
                isMetadataVisible
                  ? "Hide image metadata"
                  : "Show image metadata"
              }
            >
              <MaterialIcons
                name="info-outline"
                size={22}
                color={tokens.color.text}
              />
            </TouchableOpacity>
          ) : null}
          {mode === "view" && canEdit ? (
            <TouchableOpacity
              onPress={handleStartEdit}
              style={styles.actionButton}
              accessibilityRole="button"
              accessibilityLabel="Edit current image"
            >
              <MaterialIcons name="edit" size={18} color={tokens.color.text} />
              <Text style={styles.actionButtonText}>Edit</Text>
            </TouchableOpacity>
          ) : null}
          {enableRotation || mode === "edit" ? (
            <TouchableOpacity
              onPress={handleRotateLeft}
              style={[styles.iconButton, saving && styles.disabledControl]}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Rotate current image counterclockwise"
            >
              <MaterialIcons
                name="rotate-left"
                size={22}
                color={tokens.color.text}
              />
            </TouchableOpacity>
          ) : null}
          {enableRotation || mode === "edit" ? (
            <TouchableOpacity
              onPress={handleRotateRight}
              style={[styles.iconButton, saving && styles.disabledControl]}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Rotate current image clockwise"
            >
              <MaterialIcons
                name="rotate-right"
                size={22}
                color={tokens.color.text}
              />
            </TouchableOpacity>
          ) : null}
          {mode === "edit" ? (
            <TouchableOpacity
              onPress={handleCancelEdit}
              style={[styles.actionButton, saving && styles.disabledControl]}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Cancel image edits"
            >
              <Text style={styles.actionButtonText}>Cancel</Text>
            </TouchableOpacity>
          ) : null}
          {mode === "edit" && hasUnsavedRotation ? (
            <TouchableOpacity
              onPress={handleSaveEdit}
              style={[styles.saveButton, saving && styles.disabledControl]}
              disabled={saving}
              accessibilityRole="button"
              accessibilityLabel="Save image edits"
            >
              <Text style={styles.saveButtonText}>
                {saving ? "Saving..." : "Save Changes"}
              </Text>
            </TouchableOpacity>
          ) : null}
          {shareLinkForIndex ? (
            <TouchableOpacity
              onPress={handleCopyShareLink}
              style={styles.shareButton}
              accessibilityRole="button"
              accessibilityLabel="Copy share link for current image"
            >
              <Text style={styles.shareButtonText}>Copy Link</Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity
            onPress={onClose}
            style={styles.iconButton}
            accessibilityRole="button"
            accessibilityLabel="Close image gallery"
          >
            <MaterialIcons name="close" size={22} color={tokens.color.text} />
          </TouchableOpacity>
        </View>
      </View>
      {shareMessage ? (
        <Text style={styles.shareMessage}>{shareMessage}</Text>
      ) : null}
      {jobStatusMessage ? (
        <Text style={styles.statusMessage}>{jobStatusMessage}</Text>
      ) : null}

      <View style={styles.viewerContent}>
        <View style={styles.carouselWrap}>
          <ImageCarousel
            key={carouselKey}
            images={imageUrls}
            thumbnailUrls={thumbnailUrls}
            initialIndex={requestedIndex}
            showDownloadAllButton
            showDownloadOneButton
            showPaginationDots={false}
            showThumbnailDrawer
            rotations={rotations}
            onImageChange={(newIndex) => {
              setCurrentIndex(newIndex);
              onIndexChange?.(newIndex);
            }}
          />
        </View>
        {canShowMetadata && isMetadataVisible ? (
          <View style={[styles.metadataPanel, { width: metadataPanelWidth }]}>
            <Text style={styles.metadataTitle}>Image metadata</Text>
            <ScrollView
              style={styles.metadataScroll}
              contentContainerStyle={styles.metadataRows}
            >
              {metadataRows.map((row) => (
                <View key={row.label} style={styles.metadataRow}>
                  <Text style={styles.metadataLabel}>{row.label}</Text>
                  <Text style={styles.metadataValue}>{row.value}</Text>
                </View>
              ))}
            </ScrollView>
          </View>
        ) : null}
      </View>
    </React.Fragment>
  );

  if (!visible) return null;

  if (Platform.OS === "web") {
    if (typeof document === "undefined") return null;
    const { createPortal } = require("react-dom");

    return createPortal(
      <div
        onClick={onClose}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483000,
          background: tokens.color.overlay,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: tokens.spacing.lg,
        }}
      >
        <div
          onClick={(event) => event.stopPropagation()}
          style={{
            width: "min(1280px, calc(100vw - 32px))",
            height: "calc(100vh - 32px)",
            background: tokens.color.surface,
            borderRadius: tokens.radius.md,
            padding: tokens.spacing.lg,
            display: "flex",
            flexDirection: "column",
            overflow: "hidden",
          }}
        >
          {renderModalBody(
            `gallery-carousel-web-${visible ? "open" : "closed"}-${imageUrls.length}-${selectedIndex}`,
          )}
        </div>
      </div>,
      document.body,
    );
  }

  return (
    <Modal
      animationType="fade"
      transparent
      visible={visible}
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <View style={styles.modalCard}>
          {renderModalBody(
            `gallery-carousel-native-${visible ? "open" : "closed"}-${imageUrls.length}-${selectedIndex}`,
          )}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: tokens.color.overlay,
    justifyContent: "center",
    alignItems: "center",
    padding: tokens.spacing.lg,
  },
  modalCard: {
    width: "100%",
    maxWidth: 1280,
    height: "90%",
    backgroundColor: tokens.color.surface,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.lg,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacing.md,
    marginBottom: tokens.spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: tokens.color.border,
    paddingBottom: tokens.spacing.md,
  },
  headerTitle: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.lg,
    flexShrink: 0,
  },
  headerDateSummary: {
    color: tokens.color.muted,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    flex: 1,
    textAlign: "right",
  },
  headerControls: {
    flexDirection: "row",
    alignItems: "center",
    gap: tokens.spacing.sm,
  },
  iconButton: {
    width: 44,
    height: 44,
    borderRadius: tokens.radius.xl,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.controlSurface,
    borderWidth: 1,
    borderColor: tokens.color.border,
  },
  activeIconButton: {
    backgroundColor: tokens.color.selectedControlSurface,
    borderColor: tokens.color.primary,
  },
  shareButton: {
    minHeight: 44,
    minWidth: 96,
    borderRadius: tokens.radius.xl,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.controlSurface,
    borderWidth: 1,
    borderColor: tokens.color.border,
    paddingHorizontal: tokens.spacing.md,
  },
  actionButton: {
    minHeight: 44,
    minWidth: 80,
    borderRadius: tokens.radius.xl,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.controlSurface,
    borderWidth: 1,
    borderColor: tokens.color.border,
    paddingHorizontal: tokens.spacing.md,
    flexDirection: "row",
    gap: tokens.spacing.xs,
  },
  actionButtonText: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  saveButton: {
    minHeight: 44,
    minWidth: 132,
    borderRadius: tokens.radius.xl,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.color.primary,
    paddingHorizontal: tokens.spacing.md,
  },
  saveButtonText: {
    color: tokens.color.primaryText,
    fontFamily: "Urbanist500",
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  disabledControl: {
    opacity: 0.6,
  },
  shareButtonText: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
  shareMessage: {
    color: tokens.color.muted,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    marginBottom: tokens.spacing.sm,
  },
  statusMessage: {
    color: tokens.color.muted,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
    marginBottom: tokens.spacing.sm,
  },
  carouselWrap: {
    flex: 1,
  },
  viewerContent: {
    flex: 1,
    flexDirection: "row",
    gap: tokens.spacing.lg,
    minHeight: 0,
  },
  metadataPanel: {
    alignSelf: "stretch",
    backgroundColor: tokens.color.panelSurface,
    borderWidth: 1,
    borderColor: tokens.color.border,
    borderRadius: tokens.radius.md,
    padding: tokens.spacing.md,
  },
  metadataTitle: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.md,
    lineHeight: tokens.lineHeight.md,
    marginBottom: tokens.spacing.md,
  },
  metadataScroll: {
    flex: 1,
  },
  metadataRows: {
    gap: tokens.spacing.md,
    paddingBottom: tokens.spacing.sm,
  },
  metadataRow: {
    gap: tokens.spacing.xs,
  },
  metadataLabel: {
    color: tokens.color.muted,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.xs,
    lineHeight: tokens.lineHeight.sm,
  },
  metadataValue: {
    color: tokens.color.text,
    fontFamily: tokens.font.base,
    fontSize: tokens.fontSize.sm,
    lineHeight: tokens.lineHeight.sm,
  },
});

export default GalleryModal;
